import React, { useCallback, useRef, useState } from "react"
import PropTypes from "prop-types"
import { Button, Input, Typography, Flex, Spin, Tooltip } from "antd"
import { SendOutlined, RobotOutlined, HistoryOutlined, PlusOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import useDashChat from "../../hooks/useDashChat"
import useChatSessions from "../../hooks/useChatSessions"
import ChatMessage from "../ChatMessage/ChatMessage"
import ChatSessionsSidebar from "../ChatSessionsSidebar/ChatSessionsSidebar"
import styles from "../../style.module.css"

const { Text } = Typography

export default function DashChatWidget({ onWidgetSaved }) {
  const { t } = useTranslation()
  const chatBodyRef = useRef(null)
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const scrollToBottom = useCallback(() => {
    if (chatBodyRef.current) chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight
  }, [])

  const sessions = useChatSessions()

  const sessionControl = {
    currentSessionId: sessions.currentSessionId,
    currentMessages: sessions.currentMessages,
    onSessionCreated: (sessionId) => {
      sessions.refreshSessions()
      sessions.selectSession(sessionId)
    },
    onMessageSent: () => {},
  }

  const { input, setInput, messages, isLoading, handleSend, handleChartDismiss, handleChartOptionSelect, handleOptionSelect } = useDashChat(
    scrollToBottom,
    onWidgetSaved,
    sessionControl
  )

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const showLoadOlderBtn = sessions.currentSessionId && sessions.hasMoreOlderMessages && !sessions.historyLoading
  const showHistoryLoading = sessions.historyLoading && sessions.currentMessages === null

  function renderChatBody() {
    if (showHistoryLoading) {
      return (
        <Flex vertical align="center" justify="center" gap={8} className={styles.chatHistoryLoading}>
          <Spin size="small" />
          <Text type="secondary" className={styles.textSm}>
            {t("ai_dashboard.loading_history")}
          </Text>
        </Flex>
      )
    }
    if (sessions.historyError) {
      return (
        <Flex align="center" justify="center" className={styles.chatHistoryError}>
          <Text type="secondary" className={styles.textSm}>
            {t("ai_dashboard.history_load_error")}
          </Text>
        </Flex>
      )
    }
    return (
      <>
        {messages.map((message) => (
          <ChatMessage
            key={message.id}
            message={message}
            onChartDismiss={handleChartDismiss}
            onChartOptionSelect={handleChartOptionSelect}
            onOptionSelect={handleOptionSelect}
          />
        ))}
      </>
    )
  }

  return (
    <Flex align="stretch" className={styles.dashChatOuter}>
      <div className={styles.dashChat}>
        <Flex align="center" gap={12} className={styles.dashChatHeader}>
          <Flex align="center" justify="center" className={styles.dashAvatar}>
            <RobotOutlined className={styles.chatAvatarIcon} />
          </Flex>
          <div>
            <Text strong className={styles.dashName}>
              {t("ai_dashboard.dash_name")}
            </Text>
            <Text type="secondary" className={styles.textSm}>
              {t("ai_dashboard.dash_subtitle")}
            </Text>
          </div>
          <Flex gap={4} className={styles.chatHeaderActions}>
            <Tooltip title={t("ai_dashboard.new_chat")}>
              <Button
                type="text"
                icon={<PlusOutlined />}
                className={styles.chatHeaderBtn}
                onClick={sessions.startNewSession}
                disabled={sessions.currentSessionId === null}
              />
            </Tooltip>
            <Tooltip title={t("ai_dashboard.chat_history")}>
              <Button
                type="text"
                icon={<HistoryOutlined />}
                className={`${styles.chatHeaderBtn} ${sidebarOpen ? styles.chatHeaderBtnActive : ""}`}
                onClick={() => setSidebarOpen((open) => !open)}
              />
            </Tooltip>
          </Flex>
        </Flex>

        <Flex vertical gap="var(--spacing-ss)" className={styles.dashChatBody} ref={chatBodyRef}>
          {showLoadOlderBtn && (
            <Button type="link" size="small" onClick={sessions.loadOlderMessages} className={styles.loadOlderBtn}>
              {t("ai_dashboard.load_older_messages")}
            </Button>
          )}

          {sessions.historyLoading && sessions.currentMessages !== null && (
            <Flex justify="center" className={styles.loadOlderSpinner}>
              <Spin size="small" />
            </Flex>
          )}

          {renderChatBody()}

          {isLoading && (
            <Flex gap={8} align="flex-start" className={styles.chatMsg}>
              <Flex align="center" justify="center" className={styles.dashAvatarSm}>
                <RobotOutlined className={styles.chatAvatarIconSm} />
              </Flex>
              <div className={`${styles.chatBubble} ${styles.chatBubbleAi}`}>
                <Text type="secondary" className={styles.textSm}>
                  {t("ai_dashboard.thinking")}
                </Text>
              </div>
            </Flex>
          )}
        </Flex>

        <Flex align="center" gap={8} className={styles.dashChatInput}>
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t("ai_dashboard.input_placeholder")}
            variant="borderless"
            className={styles.chatInputField}
            disabled={isLoading || showHistoryLoading}
          />
          <Button type="primary" icon={<SendOutlined />} shape="round" loading={isLoading} onClick={handleSend}>
            <span className={styles.chatSendLabel}>{t("ai_dashboard.send")}</span>
          </Button>
        </Flex>
      </div>

      {sidebarOpen && (
        <ChatSessionsSidebar
          sessionsList={sessions.sessionsList}
          sessionsLoading={sessions.sessionsLoading}
          sessionsError={sessions.sessionsError}
          hasMoreSessions={sessions.hasMoreSessions}
          currentSessionId={sessions.currentSessionId}
          onSelectSession={sessions.selectSession}
          onLoadMore={sessions.loadMoreSessions}
          onRetry={sessions.retryFetchSessions}
          onClose={() => setSidebarOpen(false)}
          onRenameSession={sessions.renameSession}
          onDeleteSession={sessions.deleteSession}
        />
      )}
    </Flex>
  )
}

DashChatWidget.propTypes = {
  onWidgetSaved: PropTypes.func,
}
